import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ClientLayout from "../../components/client/ClientLayout";
import { useAuth } from "../../context/AuthContext";
import { clientApi } from "../../api";
import PasswordInput from "../../components/PasswordInput";
import AdminSelect from "../../components/AdminSelect";
import IdentityVerificationCard from "../../components/IdentityVerificationCard";
import FaceMatchBadge from "../../components/FaceMatchBadge";
import useUrlState from "../../hooks/useUrlState";
import { formatDateAU, formatDateTimeAU } from "../../utils/formatDate";

export default function MemberDetail() {
  const { membershipId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.clientRole === "admin";

  const [bundle, setBundle] = useState(null);
  const [passes, setPasses] = useState([]);
  const [activity, setActivity] = useState([]);
  const [idDoc, setIdDoc] = useState(undefined); // FOXID scan metadata: undefined loading, null none
  const [playSessions, setPlaySessions] = useState(null);
  const [playLoading, setPlayLoading] = useState(false);
  const [points, setPoints] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsReload, setPointsReload] = useState(0);
  const [tab, setTab] = useUrlState("tab", "profile");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Edit form state
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  // PIN reset modal
  const [showPinModal, setShowPinModal] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinBusy, setPinBusy] = useState(false);

  // Card replace wizard
  const [showCardWizard, setShowCardWizard] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const b = await clientApi.getMemberBundle(membershipId);
      setBundle(b);
      setForm(buildEditForm(b));
      const [pList, actList, docRes] = await Promise.all([
        clientApi.getMemberPasses(membershipId).catch(() => ({ passes: [] })),
        clientApi.getMemberActivity(membershipId).catch(() => []),
        clientApi.getMemberIdDocument(membershipId).catch(() => ({ document: null })),
      ]);
      setPasses(pList.passes || []);
      setActivity(actList || []);
      setIdDoc(docRes.document || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [membershipId]);

  useEffect(() => { load(); }, [load]);

  // Lazy-load play sessions the first time the Play tab is opened.
  useEffect(() => {
    if (tab !== "play" || playSessions !== null) return;
    setPlayLoading(true);
    clientApi.getMemberPlaySessions(membershipId, { limit: 50 })
      .then((d) => setPlaySessions(d.sessions || []))
      .catch(() => setPlaySessions([]))
      .finally(() => setPlayLoading(false));
  }, [tab, membershipId, playSessions]);

  // Points tab: fetch on demand, reload on award/redeem success.
  useEffect(() => {
    if (tab !== "points") return;
    setPointsLoading(true);
    clientApi.getMemberPoints(membershipId)
      .then(setPoints)
      .catch(() => setPoints({ error: "Unable to fetch" }))
      .finally(() => setPointsLoading(false));
  }, [tab, membershipId, pointsReload]);

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSaving(true); setFormError(""); setFormSuccess("");
    try {
      await clientApi.updateMember(membershipId, toS7000Payload(form));
      setFormSuccess("Saved");
      setEditing(false);
      await load();
    } catch (err) { setFormError(err.message); }
    setSaving(false);
  }

  async function handleResetPin(e) {
    e.preventDefault();
    setPinError("");
    if (!/^\d{3,10}$/.test(newPin)) return setPinError("PIN must be 3–10 digits.");
    if (newPin !== confirmPin) return setPinError("PINs don't match.");
    setPinBusy(true);
    try {
      // Fall back through: our portal's local card number → the member's
      // primary cached card (V_PUB_CARDS) → empty (S7000 accepts empty).
      const cardNumber = bundle?.local?.card_number
        || bundle?.cards?.[0]?.cardnumber
        || "";
      await clientApi.resetMemberPin(membershipId, { pin: newPin, cardNumber });
      setShowPinModal(false); setNewPin(""); setConfirmPin("");
      await load();
    } catch (err) { setPinError(err.message || "Reset failed"); }
    setPinBusy(false);
  }

  async function handleSuspend(suspend) {
    if (!window.confirm(`${suspend ? "Suspend" : "Re-activate"} this member?`)) return;
    try {
      if (suspend) await clientApi.suspendMember(membershipId, {});
      else await clientApi.unsuspendMember(membershipId);
      await load();
    } catch (err) { alert(err.message); }
  }

  if (loading) return <ClientLayout><div className="loading-state"><div className="spinner" /></div></ClientLayout>;
  if (error)   return <ClientLayout><div className="toast toast-error">{error}</div></ClientLayout>;
  if (!bundle) return <ClientLayout><div className="empty-state"><h3>Not found</h3></div></ClientLayout>;

  const s = bundle.s7000 || {};
  const local = bundle.local || {};
  const cache = bundle.cache || {};
  const cards = bundle.cards || [];

  // Merge order for display-level fields: live > cache > local.
  const name = `${s.firstName || cache.firstname || local.first_name || ""} ${s.lastName || cache.surname || local.last_name || ""}`.trim();
  const email = s.email || cache.email || local.email || "";
  const status = s.memberStatus != null ? String(s.memberStatus) : (cache.membershipstatus || local.status || "");
  const suspended = String(s.suspended || cache.suspensionflag || "") === "1" || status === "3";
  // Expiry can live on the live shape (S7000/IGT dateExpiry), the provider
  // cache, the first card, or the local portal row depending on provider —
  // fall through so CircleScan (whose standing is expiry-derived) still shows a
  // date and can flag an Expired member instead of always reading "Active".
  const expiry = s.dateExpiry || cache.dateexpiry || cards[0]?.cardexpirydate || local.date_expiry || null;
  const expDate = expiry ? new Date(expiry) : null;
  const isExpired = !suspended && expDate && !Number.isNaN(expDate.getTime()) && expDate < new Date();
  // Provider isolation: each venue's portal shows only its provider's
  // features. S7000-specific actions/tabs (Play, Points, Reset PIN,
  // Replace card, Suspend, the inline S7000 edit form) gate on isS7000;
  // IGT actions gate on isIgt; shared features (Profile, Passes, Activity)
  // show for all. A future provider shows only the shared set until its
  // own capabilities are added here.
  const provider = bundle?.provider_type || "s7000";
  const isIgt   = provider === "igt";
  const isS7000 = provider === "s7000";
  // Digital membership (Litecard) toggle — hide the Passes tab when the venue
  // has it off (default ON).
  const digitalPassesOn = bundle?.digital_passes_enabled !== false;
  const providerLabel = provider === "igt" ? "IGT" : provider === "circlescan" ? "CircleScan" : provider === "swiftpos" ? "SwiftPOS" : "S7000";
  const joined = s.dateOfJoining || cache.dateregistered || local.created_at || null;
  const cardNumber = s.barCode || cards[0]?.cardnumber || local.card_number || "";

  return (
    <ClientLayout>
      <button className="btn btn-outline btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: "1rem" }}>← Back</button>

      <div className="md-header">
        <div className="md-header-top">
          <div className="md-avatar">
            {bundle.photoDataUrl ? <img src={bundle.photoDataUrl} alt="" /> : ((name || "?")[0] || "?").toUpperCase()}
          </div>
          <div className="md-header-id">
            <h1 className="md-header-name">{name || `Member #${membershipId}`}</h1>
            <div className="md-header-sub">Membership #{membershipId} · {providerLabel}{email ? ` · ${email}` : ""}</div>
            <div className="md-pills">
              <span className={`md-pill ${suspended ? "bad" : isExpired ? "warn" : "ok"}`}>{suspended ? "Suspended" : isExpired ? "Expired" : "Active"}</span>
              {idDoc?.face_match_status && <FaceMatchBadge status={idDoc.face_match_status} size="sm" />}
            </div>
          </div>
          <div className="md-header-actions">
            {isIgt && isAdmin && (
              <button className="btn btn-outline btn-sm" onClick={() => navigate(`/client/igt/update-profile?id=${membershipId}`)}>Edit profile</button>
            )}
            {isS7000 && <button className="btn btn-outline btn-sm" onClick={() => setShowPinModal(true)}>Reset PIN</button>}
            {isS7000 && <button className="btn btn-outline btn-sm" onClick={() => setShowCardWizard(true)}>Replace card</button>}
            {isS7000 && isAdmin && (
              suspended
                ? <button className="btn btn-primary btn-sm" onClick={() => handleSuspend(false)}>Reactivate</button>
                : <button className="btn btn-danger btn-sm" onClick={() => handleSuspend(true)}>Suspend</button>
            )}
          </div>
        </div>
        {(joined || expiry || cardNumber) && (
          <div className="md-facts">
            {joined && <div><span className="md-fact-label">Joined</span><div className="md-fact-value">{formatDateAU(joined)}</div></div>}
            {expiry && <div><span className="md-fact-label">Expires</span><div className="md-fact-value">{formatDateAU(expiry)}</div></div>}
            {cardNumber && <div><span className="md-fact-label">Card number</span><div className="md-fact-value">{cardNumber}</div></div>}
          </div>
        )}
      </div>

      <div>
          <div className="tabs">
            <button className={tab === "profile" ? "active" : ""} onClick={() => setTab("profile")}>Profile</button>
            {digitalPassesOn && <button className={tab === "passes" ? "active" : ""} onClick={() => setTab("passes")}>Passes ({passes.length})</button>}
            {isS7000 && <button className={tab === "play" ? "active" : ""} onClick={() => setTab("play")}>Play</button>}
            {isS7000 && <button className={tab === "points" ? "active" : ""} onClick={() => setTab("points")}>Points</button>}
            <button className={tab === "activity" ? "active" : ""} onClick={() => setTab("activity")}>Activity ({activity.length})</button>
            {idDoc && <button className={tab === "identity" ? "active" : ""} onClick={() => setTab("identity")}>Identity</button>}
          </div>

          {tab === "profile" && (
            <div style={{ marginTop: "1rem" }}>
              {formError && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{formError}</div>}
              {formSuccess && <div className="toast toast-success" style={{ marginBottom: "0.75rem" }}>{formSuccess}</div>}
              {!editing ? (
                <>
                  <div className="md-stack">
                    <ProfileGrid s7000={s} local={local} cache={cache} cards={cards} titleName={bundle.titleName} provider={provider} />
                    <TermsAcknowledgement local={local} />
                  </div>
                  {/* Inline edit writes via the S7000 update endpoint. IGT
                      members edit through the IGT "Edit profile" tool
                      (header button) instead, so hide this for non-S7000. */}
                  {isS7000 && <button className="btn btn-primary" style={{ marginTop: "1rem" }} onClick={() => setEditing(true)}>Edit details</button>}
                </>
              ) : (
                <form onSubmit={handleSaveProfile}>
                  <EditForm form={form} setForm={setForm} />
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
                    <button type="button" className="btn btn-outline" onClick={() => { setEditing(false); setForm(buildEditForm(bundle)); setFormError(""); }}>Cancel</button>
                  </div>
                </form>
              )}
            </div>
          )}

          {tab === "passes" && digitalPassesOn && (
            <PassesTab
              passes={passes}
              membershipId={membershipId}
              isAdmin={isAdmin}
              onReload={load}
            />
          )}

          {tab === "play" && isS7000 && <PlayTab loading={playLoading} sessions={playSessions || []} />}

          {tab === "points" && isS7000 && (
            <PointsTab
              loading={pointsLoading}
              data={points}
              membershipId={membershipId}
              isAdmin={isAdmin}
              onChange={() => setPointsReload((n) => n + 1)}
            />
          )}

          {tab === "activity" && <ActivityTab rows={activity} />}

          {tab === "identity" && (
            <div style={{ marginTop: "1rem" }}>
              <IdentityVerificationCard doc={idDoc} loadImage={(which) => clientApi.getMemberIdImage(membershipId, which)} />
            </div>
          )}
        </div>

      {showPinModal && (
        <Modal title="Reset PIN" onClose={() => setShowPinModal(false)}>
          <form onSubmit={handleResetPin}>
            {pinError && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{pinError}</div>}
            <div className="form-field">
              <label>New PIN <span className="required">*</span></label>
              <PasswordInput maxLength={10} inputMode="numeric" pattern="\d*" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} placeholder="3–10 digits" required />
            </div>
            <div className="form-field">
              <label>Confirm PIN <span className="required">*</span></label>
              <PasswordInput maxLength={10} inputMode="numeric" pattern="\d*" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))} required />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowPinModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={pinBusy}>{pinBusy ? "Resetting…" : "Reset PIN"}</button>
            </div>
          </form>
        </Modal>
      )}

      {showCardWizard && (
        <CardReplaceWizard
          membershipId={membershipId}
          currentCard={local.card_number}
          onClose={() => setShowCardWizard(false)}
          onDone={async () => { setShowCardWizard(false); await load(); }}
        />
      )}
    </ClientLayout>
  );
}

/* Renders the captured T&Cs acknowledgement (timestamp, version, IP,
 * user agent, signature image) when this member was joined / last
 * renewed under a T&Cs-required venue. Hidden when the venue didn't
 * require acknowledgement at that time. */
function TermsAcknowledgement({ local }) {
  if (!local) return null;
  if (!local.terms_accepted_at) return null;
  return (
    <section className="md-section">
      <div className="md-section-head">
        <span>Terms &amp; Conditions</span>
        <span className="md-pill ok">Accepted</span>
      </div>
      <div className="md-field-grid">
        <div><div className="md-field-label">Accepted at</div><div className="md-field-value">{formatDateTimeAU(local.terms_accepted_at)}</div></div>
        {local.terms_version     && <div><div className="md-field-label">Version</div><div className="md-field-value">{local.terms_version}</div></div>}
        {local.terms_signed_name && <div><div className="md-field-label">Signed by</div><div className="md-field-value">{local.terms_signed_name}</div></div>}
        {local.terms_accepted_ip && <div><div className="md-field-label">IP address</div><div className="md-field-value">{local.terms_accepted_ip}</div></div>}
      </div>
      {local.terms_user_agent && (
        <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.5rem", wordBreak: "break-all" }}>
          <strong>User agent:</strong> {local.terms_user_agent}
        </div>
      )}
      {local.terms_signature && (
        <div style={{ marginTop: "0.65rem" }}>
          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.3rem" }}>Signature</div>
          <img
            src={local.terms_signature}
            alt="Member signature"
            style={{ maxWidth: "100%", maxHeight: 140, background: "#fff", border: "1px solid var(--border)", borderRadius: 6, padding: 4 }}
          />
        </div>
      )}
    </section>
  );
}

// Render a profile value. Strip the time portion ONLY for ISO date/datetime
// values (e.g. "1985-06-10T00:00:00Z" → "1985-06-10"); leave text untouched
// so names containing a "T" (e.g. "Tahlia") aren't truncated at the split.
function fmtFieldValue(v) {
  if (v === undefined || v === null || v === "") return "—";
  const s = String(v);
  // ISO date or datetime → AU dd/mm/yyyy; non-date strings pass through
  // unchanged (so values containing a "T", e.g. "Tahlia", aren't mangled).
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(s) ? formatDateAU(s) : s;
}

function ProfileGrid({ s7000, local, cache, cards, titleName, provider }) {
  // Merge priority (each field): live S7000 → cache → local portal.
  // Live is freshest, cache fills gaps for members never touched via
  // our portal, local catches the ones we created but haven't synced.
  const firstCard = (cards && cards[0]) || {};
  const carriesState = provider === "s7000" || provider === "igt";
  const pick = (fromLive, fromCache, fromLocal) =>
    (fromLive !== undefined && fromLive !== null && fromLive !== "") ? fromLive
    : (fromCache !== undefined && fromCache !== null && fromCache !== "") ? fromCache
    : (fromLocal !== undefined && fromLocal !== null && fromLocal !== "") ? fromLocal
    : "";

  const membership = [
    // IGT's profile GET doesn't expose a join date, so fall back to the
    // local portal row's created_at (when the member joined via signup).
    ["Date of joining", pick(s7000.dateOfJoining, cache.dateregistered, local.created_at)],
    // Card expiry is S7000's authoritative expiry (local.date_expiry is
    // our portal's membership expiry, which is a different concept).
    ["Card expiry",     pick(s7000.dateExpiry,   firstCard.cardexpirydate, local.date_expiry)],
    ["Card number",     pick(s7000.barCode,      firstCard.cardnumber,     local.card_number)],
  ];
  const personal = [
    // titleName is resolved server-side (live name, or the numeric id mapped
    // through igt_titles) so this never shows a bare number.
    ["Title",           titleName || pick(s7000.title, cache.title, local.title)],
    ["First name",      pick(s7000.firstName,    cache.firstname,    local.first_name)],
    ["Last name",       pick(s7000.lastName,     cache.surname,      local.last_name)],
    ["Preferred name",  pick(s7000.preferredName, cache.preferredname, "")],
    ["Date of birth",   pick(s7000.dateOfBirth,  cache.dateofbirth,  local.date_of_birth)],
    ["Gender",          pick(s7000.gender,       cache.gender,       local.gender)],
    ["Occupation",      pick(s7000.occupation,   cache.occupation,   local.occupation)],
  ];
  const contact = [
    ["Email",           pick(s7000.email,        cache.email,        local.email)],
    ["Mobile",          pick(s7000.mobile,       cache.mobile,       local.mobile)],
    ["Telephone",       pick(s7000.telephone,    cache.telephone,    local.telephone)],
  ];
  const address = [
    ["Address 1",       pick(s7000.address1,     cache.addressline1, local.address_line1)],
    ["Address 2",       pick(s7000.address2,     cache.addressline2, local.address_line2)],
    // State is an S7000/IGT-only address component; CircleScan addresses are
    // flat (Address + Suburb + Postcode), so omit it rather than show a blank.
    ...(carriesState ? [["State", pick(s7000.address3, cache.addressline3, local.state)]] : []),
    ["Postcode",        pick(s7000.postCode,     cache.postcode,     local.postcode)],
  ];
  return (
    <>
      <DetailSection title="Membership" items={membership} />
      <DetailSection title="Personal" items={personal} />
      <DetailSection title="Contact" items={contact} />
      <DetailSection title="Address" items={address} />
    </>
  );
}

/* A titled card of label/value fields. Empty fields (and wholly-empty
   sections) are dropped so the profile stays clean instead of a wall of —. */
function DetailSection({ title, items, action }) {
  const shown = (items || []).filter(([, v]) => v !== "" && v != null && v !== undefined);
  if (!shown.length && !action) return null;
  return (
    <section className="md-section">
      <div className="md-section-head">
        <span>{title}</span>
        {action}
      </div>
      <div className="md-field-grid">
        {shown.map(([k, v]) => (
          <div key={k} className="md-field">
            <div className="md-field-label">{k}</div>
            <div className="md-field-value">{fmtFieldValue(v)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function EditForm({ form, setForm }) {
  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0.75rem" }}>
      {[
        ["title", "Title"], ["firstName", "First name"], ["lastName", "Last name"],
        ["gender", "Gender"], ["email", "Email"], ["mobile", "Mobile"],
        ["telephone", "Telephone"], ["address1", "Street"], ["address2", "Suburb"],
        ["address3", "State"], ["postCode", "Postcode"], ["occupation", "Occupation"],
      ].map(([k, label]) => (
        <div key={k} className="form-field">
          <label>{label}</label>
          <input value={form[k] || ""} onChange={set(k)} />
        </div>
      ))}
    </div>
  );
}

function buildEditForm(b) {
  const s = b?.s7000 || {};
  const cache = b?.cache || {};
  const local = b?.local || {};
  const pick = (a, c, l) => a || c || l || "";
  return {
    title:      pick(s.title,      cache.title,        local.title),
    firstName:  pick(s.firstName,  cache.firstname,    local.first_name),
    lastName:   pick(s.lastName,   cache.surname,      local.last_name),
    gender:     pick(s.gender,     cache.gender,       local.gender),
    email:      pick(s.email,      cache.email,        local.email),
    mobile:     pick(s.mobile,     cache.mobile,       local.mobile),
    telephone:  pick(s.telephone,  cache.telephone,    local.telephone),
    address1:   pick(s.address1,   cache.addressline1, local.address_line1),
    address2:   pick(s.address2,   cache.addressline2, local.address_line2),
    address3:   pick(s.address3,   cache.addressline3, local.state),
    postCode:   pick(s.postCode,   cache.postcode,     local.postcode),
    occupation: pick(s.occupation, cache.occupation,   local.occupation),
  };
}

function toS7000Payload(f) {
  // Only forward non-empty fields so we don't overwrite with "".
  const out = {};
  for (const [k, v] of Object.entries(f || {})) {
    if (v !== "" && v != null) out[k] = v;
  }
  return out;
}

function PassesTab({ passes, membershipId, isAdmin, onReload }) {
  const [busy, setBusy] = useState(false);

  // Re-issue semantics: if there's already an active pass, the next
  // issue should supersede it (the backend deactivates existing actives
  // before creating the new one) — keeps wallets to one ACTIVE at a time.
  const hasActive = passes.some((p) => (p.status || "").toUpperCase() === "ACTIVE");
  const buttonLabel = hasActive ? "Re-issue pass" : "Issue pass";
  const busyLabel   = hasActive ? "Re-issuing…"  : "Issuing…";

  async function issue() {
    if (hasActive && !window.confirm("This will deactivate the current active pass and create a new one. Continue?")) return;
    setBusy(true);
    try {
      await clientApi.issueMemberPass(membershipId, hasActive ? "reissue" : "issue");
      await onReload();
    } catch (err) { alert(err.message); }
    setBusy(false);
  }

  async function setStatus(id, status) {
    if (!window.confirm(`Set pass status to ${status}?`)) return;
    try { await clientApi.updatePassStatus(id, status); await onReload(); }
    catch (err) { alert(err.message); }
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      {passes.length ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Issued</th><th>Status</th><th>Links</th><th></th></tr></thead>
            <tbody>
              {passes.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{formatDateTimeAU(p.created_at)}</td>
                  <td><span className={`badge ${p.status === "ACTIVE" ? "active" : "inactive"}`}>{p.status}</span></td>
                  <td>
                    {p.apple_link  && <a href={p.apple_link}  target="_blank" rel="noreferrer" style={{ marginRight: "0.75rem" }}>Apple</a>}
                    {p.google_link && <a href={p.google_link} target="_blank" rel="noreferrer">Google</a>}
                  </td>
                  <td>
                    {isAdmin && p.status === "ACTIVE"   && <button className="btn btn-outline btn-sm" onClick={() => setStatus(p.id, "DEACTIVATED")}>Deactivate</button>}
                    {isAdmin && p.status !== "ACTIVE"   && <button className="btn btn-outline btn-sm" onClick={() => setStatus(p.id, "ACTIVE")}>Activate</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div className="empty-state"><p>No passes issued for this member yet.</p></div>}
      <button className="btn btn-primary" style={{ marginTop: "1rem" }} disabled={busy} onClick={issue}>
        {busy ? busyLabel : buttonLabel}
      </button>
    </div>
  );
}

function PlayTab({ loading, sessions }) {
  if (loading) return <div style={{ color: "var(--text-muted)", marginTop: "1rem" }}>Loading play sessions from S7000…</div>;
  if (!sessions.length) return <div className="empty-state" style={{ marginTop: "1rem" }}><p>No play sessions recorded for this member in S7000.</p></div>;

  const totals = sessions.reduce((acc, s) => {
    const netWin = Number(s.netwin || s.netWin || 0);
    const turnover = Number(s.turnover || 0);
    return { netWin: acc.netWin + netWin, turnover: acc.turnover + turnover };
  }, { netWin: 0, turnover: 0 });

  const lastVisit = sessions[0]?.starttime || sessions[0]?.startTime;

  return (
    <div style={{ marginTop: "1rem" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.5rem", marginBottom: "1rem" }}>
        <div className="stat-card" style={{ padding: "0.75rem 1rem" }}>
          <span className="stat-label">Sessions</span>
          <span className="stat-value" style={{ fontSize: "1.2rem" }}>{sessions.length}</span>
        </div>
        <div className="stat-card" style={{ padding: "0.75rem 1rem" }}>
          <span className="stat-label">Last visit</span>
          <span className="stat-value" style={{ fontSize: "1rem" }}>{lastVisit ? new Date(lastVisit).toLocaleDateString() : "—"}</span>
        </div>
        <div className="stat-card" style={{ padding: "0.75rem 1rem" }}>
          <span className="stat-label">Total turnover</span>
          <span className="stat-value" style={{ fontSize: "1.05rem" }}>{totals.turnover ? `$${(totals.turnover).toFixed(2)}` : "—"}</span>
        </div>
        <div className="stat-card" style={{ padding: "0.75rem 1rem" }}>
          <span className="stat-label">Net win</span>
          <span className="stat-value" style={{ fontSize: "1.05rem", color: totals.netWin < 0 ? "var(--warning)" : "var(--success)" }}>
            {totals.netWin ? `$${totals.netWin.toFixed(2)}` : "—"}
          </span>
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Start</th><th>End</th><th>EGM</th><th>Turnover</th><th>Net win</th></tr></thead>
          <tbody>
            {sessions.map((s, i) => {
              const start = s.starttime || s.startTime;
              const end   = s.endtime   || s.endTime;
              const egm   = s.egmid     || s.egmId || s.assetid || "—";
              const turnover = s.turnover;
              const netwin   = s.netwin ?? s.netWin;
              return (
                <tr key={s.sessionid || s.sessionId || i}>
                  <td style={{ fontSize: "0.85rem" }}>{start ? new Date(start).toLocaleString() : "—"}</td>
                  <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{end ? new Date(end).toLocaleString() : "—"}</td>
                  <td><code style={{ fontSize: "0.8rem" }}>{egm}</code></td>
                  <td style={{ fontWeight: 600 }}>{turnover != null ? `$${Number(turnover).toFixed(2)}` : "—"}</td>
                  <td style={{ fontWeight: 600, color: netwin != null && Number(netwin) < 0 ? "var(--warning)" : undefined }}>
                    {netwin != null ? `$${Number(netwin).toFixed(2)}` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PointsTab({ loading, data, membershipId, isAdmin, onChange }) {
  const [mode, setMode] = useState(null); // "award" | "redeem"
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  if (loading) return <div style={{ color: "var(--text-muted)", marginTop: "1rem" }}>Loading points balance…</div>;

  const balance = data?.balance && !data.balance.error
    ? (data.balance.balance ?? data.balance.pointBalance ?? data.balance.points ?? null)
    : null;
  const history = Array.isArray(data?.history) ? data.history : [];
  const notEnrolled = data?.balance?.error && /no.*point|not.*found/i.test(data.balance.error);

  async function submit() {
    setError(""); setFlash("");
    const n = Number(amount);
    if (!n || n <= 0) return setError("Amount must be a positive number.");
    setBusy(true);
    try {
      if (mode === "award") {
        await clientApi.awardMemberPoints(membershipId, { points: n, reason });
        setFlash(`Awarded ${n} points.`);
      } else {
        await clientApi.redeemMemberPoints(membershipId, { points: n, reason });
        setFlash(`Redeemed ${n} points.`);
      }
      setMode(null); setAmount(""); setReason("");
      onChange();
    } catch (err) { setError(err.message); }
    setBusy(false);
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      {flash && <div className="toast toast-success" style={{ marginBottom: "0.75rem" }}>{flash}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.5rem", marginBottom: "1rem" }}>
        <div className="stat-card">
          <span className="stat-label">Current balance</span>
          <span className="stat-value">{balance != null ? balance : "—"}</span>
          <span className="stat-sub">{data?.pointType || "BONUS"} points</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Ledger rows</span>
          <span className="stat-value">{history.length}</span>
          <span className="stat-sub">From V_PUB_MEM_POINT_BALANCE</span>
        </div>
      </div>

      {notEnrolled && (
        <div className="toast toast-error" style={{ marginBottom: "1rem" }}>
          This member has no S7000 points account yet. Points records are created once a member plays at a physical EGM.
        </div>
      )}

      {isAdmin && (
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
          <button className="btn btn-primary" onClick={() => { setMode("award"); setError(""); }}>Award points</button>
          <button className="btn btn-outline" onClick={() => { setMode("redeem"); setError(""); }}>Redeem points</button>
        </div>
      )}

      {mode && (
        <div className="settings-card" style={{ marginBottom: "1rem" }}>
          <div className="settings-card-header">
            <h3 style={{ margin: 0, textTransform: "capitalize" }}>{mode} points</h3>
            <p style={{ margin: "0.25rem 0 0" }}>Logged to activity timeline with your user id.</p>
          </div>
          {error && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}
          <div className="form-grid-2">
            <div className="form-field">
              <label>Points <span className="required">*</span></label>
              <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
            </div>
            <div className="form-field">
              <label>Reason (optional)</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. promo draw, tier bump" />
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
            <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? "Processing…" : `Confirm ${mode}`}</button>
            <button className="btn btn-outline" onClick={() => { setMode(null); setAmount(""); setReason(""); }}>Cancel</button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Type</th><th>Balance</th><th>Last updated</th></tr></thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i}>
                  <td>{h.pointtype || h.pointType || "—"}</td>
                  <td style={{ fontWeight: 600 }}>{h.pointbalance ?? h.balance ?? "—"}</td>
                  <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{h.lastupdatedtime ? new Date(h.lastupdatedtime).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActivityTab({ rows }) {
  if (!rows.length) return <div className="empty-state"><p>No activity recorded yet.</p></div>;
  return (
    <div className="table-wrap" style={{ marginTop: "1rem" }}>
      <table className="data-table">
        <thead><tr><th>When</th><th>Action</th><th>Status</th><th>By</th><th>Details</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{formatDateTimeAU(r.created_at)}</td>
              <td style={{ fontWeight: 500 }}>{formatAction(r.action)}</td>
              <td><span className={`badge ${r.status}`}>{r.status}</span></td>
              <td style={{ fontSize: "0.85rem" }}>{r.actor_name || r.actor_email || (r.action === "join" ? "Public signup" : "—")}</td>
              <td style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{r.error_message || summarizeMeta(r.metadata)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ACTION_LABELS = {
  join: "Join",
  update: "Update",
  profile_update: "Profile Update",
  pin_change: "PIN Change",
  pin_verify: "PIN Verify",
  pin_reset: "PIN Reset",
  points_award: "Points Award",
  points_redeem: "Points Redeem",
  suspend: "Suspend",
  unsuspend: "Unsuspend",
  card_replace: "Card Replace",
  card_reissue: "Card Reissue",
  renewal_blocked: "Renewal Blocked",
  merge_loser: "Merge (Loser)",
  merge_survivor: "Merge (Survivor)",
};

// Render raw action codes (e.g. "card_reissue") as friendly labels.
// Falls back to Title Case for any action not explicitly mapped.
function formatAction(action) {
  if (!action) return "—";
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  return action
    .split("_")
    .map((w) => (w === "pin" ? "PIN" : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

function summarizeMeta(meta) {
  if (!meta) return "";
  if (typeof meta === "string") return meta;
  const parts = [];
  if (meta.reason) parts.push(meta.reason);
  if (meta.old_card && meta.new_card) parts.push(`${meta.old_card} → ${meta.new_card}`);
  if (Array.isArray(meta.fields)) parts.push(`updated: ${meta.fields.join(", ")}`);
  if (meta.new_status) parts.push(meta.new_status);
  return parts.join(" · ");
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-header-text" style={{ flex: 1 }}>
            <h2>{title}</h2>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function CardReplaceWizard({ membershipId, currentCard, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [oldCard, setOldCard] = useState(currentCard || "");
  const [reason, setReason] = useState("Lost");
  const [newCardNumber, setNewCardNumber] = useState("");
  const [newPin, setNewPin] = useState("");
  const [reissuePass, setReissuePass] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setBusy(true); setError("");
    try {
      await clientApi.replaceMemberCard(membershipId, {
        oldCardNumber: oldCard,
        newCardNumber,
        reason,
        newPin: newPin || undefined,
        reissuePass,
      });
      await onDone();
    } catch (err) { setError(err.message); }
    setBusy(false);
  }

  return (
    <Modal title="Replace card" onClose={onClose}>
      {error && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}
      <div style={{ marginBottom: "1rem", color: "var(--text-muted)", fontSize: "0.85rem" }}>Step {step} of 3</div>

      {step === 1 && (
        <>
          <div className="form-field">
            <label>Old card number</label>
            <input value={oldCard} onChange={(e) => setOldCard(e.target.value)} placeholder="Current card number" />
          </div>
          <div className="form-field">
            <label>Reason</label>
            <AdminSelect
              value={reason}
              onChange={setReason}
              options={[
                { value: "Lost", label: "Lost" },
                { value: "Stolen", label: "Stolen" },
                { value: "Damaged", label: "Damaged" },
                { value: "Other", label: "Other" },
              ]}
            />
          </div>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={() => setStep(2)}>Next</button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="form-field">
            <label>New card number <span className="required">*</span></label>
            <input value={newCardNumber} onChange={(e) => setNewCardNumber(e.target.value.replace(/\s+/g, ""))} placeholder="Scan or type new card number" autoFocus required />
          </div>
          <div className="form-field">
            <label>Set a new PIN (optional)</label>
            <PasswordInput maxLength={10} inputMode="numeric" pattern="\d*" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} placeholder="3–10 digits" />
          </div>
          <div className="form-field" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <input type="checkbox" id="reissue" checked={reissuePass} onChange={(e) => setReissuePass(e.target.checked)} />
            <label htmlFor="reissue" style={{ margin: 0 }}>Reissue the digital pass</label>
          </div>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setStep(1)}>Back</button>
            <button className="btn btn-primary" disabled={!newCardNumber} onClick={() => setStep(3)}>Next</button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div style={{ padding: "0.75rem", borderRadius: 8, background: "var(--bg-elevated)" }}>
            <div><strong>Reason:</strong> {reason}</div>
            <div><strong>Old card:</strong> {oldCard || "—"}</div>
            <div><strong>New card:</strong> {newCardNumber}</div>
            <div><strong>New PIN:</strong> {newPin ? "(will be set)" : "no change"}</div>
            <div><strong>Reissue pass:</strong> {reissuePass ? "yes" : "no"}</div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setStep(2)}>Back</button>
            <button className="btn btn-primary" disabled={busy} onClick={submit}>{busy ? "Submitting…" : "Confirm & replace"}</button>
          </div>
        </>
      )}
    </Modal>
  );
}
